import { useState, useEffect } from 'react'
import { WifiOff, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [showReconnecting, setShowReconnecting] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let reconnectTimer: NodeJS.Timeout | null = null

    const handleOnline = () => {
      console.log('Connection restored')
      setIsOnline(true)
      setShowReconnecting(false)
      setRetryCount(0)

      toast.success('Connessione ripristinata!', {
        icon: '🌐',
        duration: 3000
      })

      // Sync queued messages if service worker is available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SYNC_MESSAGES'
        })
      }
    }

    const handleOffline = () => {
      console.log('Connection lost')
      setIsOnline(false)
      setShowReconnecting(true)

      toast.error('Connessione persa. Modalità offline attiva.', {
        icon: '📡',
        duration: 5000
      })

      // Start retry attempts
      let attempts = 0
      const maxAttempts = 10

      reconnectTimer = setInterval(() => {
        attempts++
        setRetryCount(attempts)

        if (attempts >= maxAttempts) {
          clearInterval(reconnectTimer!)
          setShowReconnecting(false)
        }
      }, 5000)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Periodically check actual connectivity (not just network interface)
    const connectivityCheckInterval = setInterval(() => {
      checkConnectivity()
    }, 30000) // Check every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (reconnectTimer) {
        clearInterval(reconnectTimer)
      }
      clearInterval(connectivityCheckInterval)
    }
  }, [])

  const checkConnectivity = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/public/health`, {
        method: 'GET',
        cache: 'no-cache'
      })

      if (response.ok && !isOnline) {
        window.dispatchEvent(new Event('online'))
      }
    } catch (error) {
      if (isOnline) {
        window.dispatchEvent(new Event('offline'))
      }
    }
  }

  const handleRetryNow = () => {
    setShowReconnecting(true)
    checkConnectivity()
  }

  if (isOnline) {
    return null
  }

  return (
    <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
      <div className="bg-amber-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative">
            <WifiOff className="w-5 h-5" />
            {showReconnecting && (
              <div className="absolute -right-1 -top-1">
                <RefreshCw className="w-3 h-3 animate-spin" />
              </div>
            )}
          </div>

          <div className="flex-1">
            <p className="font-medium text-sm">
              {showReconnecting ? 'Riconnessione in corso...' : 'Sei offline'}
            </p>
            <p className="text-xs text-amber-100">
              {showReconnecting
                ? `Tentativo ${retryCount}/10`
                : 'Le conversazioni salvate sono disponibili'}
            </p>
          </div>
        </div>

        {!showReconnecting && (
          <button
            onClick={handleRetryNow}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Riprova
          </button>
        )}
      </div>
    </div>
  )
}

export default OfflineIndicator
